import React, { useEffect } from 'react';
import ArticleLayout from './ArticleLayout';
import Footer from '../../../../components/Footer/Footer';
import { AssetUrls } from 'config/assets';
import { Link } from 'react-router';
import { useGetEconomicMetricsQuery } from 'store/services/economic/economicApi';
import './articles.css';

const HowItWorks = () => {
  // Fetch current economic metrics for dynamic content
  const { data: economicData } = useGetEconomicMetricsQuery();

  // SEO: Set document title and meta tags
  useEffect(() => {
    document.title = 'How TradeMagic Works - Complete Trading Guide';

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Learn how to trade Magic: The Gathering cards on TradeMagic. Complete guide covering asynchronous trading, point system, shipping, and case resolution.'
      );
    }

    // Update Open Graph meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'How TradeMagic Works - Complete Trading Guide');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute(
        'content',
        'Learn how to trade Magic: The Gathering cards on TradeMagic. Complete guide covering asynchronous trading, point system, shipping, and case resolution.'
      );
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://trademagic.gg/articles/how-it-works');
    }
  }, []);

  return (
    <ArticleLayout title="How TradeMagic Works" hero={AssetUrls.login.background}>
      {/* Schema.org markup for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'How TradeMagic Works',
          description:
            'Learn how to trade Magic: The Gathering cards on TradeMagic. Complete guide covering asynchronous trading, point system, shipping, and case resolution.',
          url: 'https://trademagic.gg/articles/how-it-works',
          publisher: {
            '@type': 'Organization',
            name: 'TradeMagic',
            url: 'https://trademagic.gg',
          },
        })}
      </script>

      <div className="article-intro">
        <p>
          TradeMagic is a community-driven trading platform that lets you trade Magic: The Gathering cards with
          collectors worldwide—without haggling, negotiating, or waiting for someone to want exactly what you have.
        </p>
      </div>

      <article className="article-content">
        {/* Section: Core Concept */}
        <section className="article-section">
          <h2>The Core Concept: Asynchronous Trading</h2>
          <p>
            Here's the key difference: Trades happen <strong>asynchronously</strong> using Trade Credits (points).
          </p>

          <p>
            <strong>What this means in practice:</strong>
          </p>

          <ul className="concept-list">
            <li>
              <strong>Getting cards:</strong> Add cards to your{' '}
              <Link to="/my-cards" className="article-link">
                Want List
              </Link>
              . If you have enough points, any trader can send them to you at any time. No confirmation needed, no
              back-and-forth negotiation.
            </li>
            <li>
              <strong>Earning points:</strong> Browse cards other people want. Send them what they're looking for. When
              the cards arrive and get checked in, you earn points automatically.
            </li>
          </ul>

          <p className="summary">
            <strong>In summary:</strong> Trade away cards you don't need, earn points, then spend those points on cards
            you actually want.
          </p>
        </section>

        {/* Section: Getting Started */}
        <section className="article-section">
          <h2>Getting Started in 3 Steps</h2>

          <div className="step">
            <h3>
              1. Build Your{' '}
              <Link to="/my-cards" className="article-link">
                Want List
              </Link>
            </h3>
            <p>Add the cards you're hunting for. If you want, you can be specific about:</p>
            <ul>
              <li>Set/edition</li>
              <li>Condition (NM, LP, MP)</li>
              <li>
                Language <em>(Support for this coming soon)</em>
              </li>
              <li>Foil vs non-foil</li>
            </ul>
            <p>The more cards you add, the faster traders will find matches and send to you.</p>
          </div>

          <div className="step">
            <h3>2. Send Cards to Earn Points</h3>
            <p>
              This is where the magic happens. The{' '}
              <Link to="/sendcards" className="article-link">
                Send page
              </Link>{' '}
              shows you everyone's{' '}
              <Link to="/my-cards" className="article-link">
                Want Lists
              </Link>{' '}
              and highlights cards you can send right now.
            </p>
            <p>
              <strong>When you find a match:</strong>
            </p>
            <ol>
              <li>Click "Initiate Trade"</li>
              <li>
                Ship within 72 hours (use our{' '}
                <Link to="/articles/shipping" className="article-link">
                  Shipping Guide
                </Link>
                )
              </li>
              <li>Recipient checks in the card</li>
              <li>You earn points automatically</li>
            </ol>
            <p className="emphasis">
              That's it. No waiting for approval, no haggling, no coordinating with the other person.
            </p>
          </div>

          <div className="step">
            <h3>
              3. Check in Cards you Receive
            </h3>
            <p>
              When you get cards in the mail, complete the trade right away so the sender gets credit.
            </p>
            <ol>
              <li>Examine the cards and make sure they're the right version and condition.</li>
              <li>
                Find the trade on your{' '}
                <Link to="/receiving" className="article-link">
                  Receiving page
                </Link>{' '}
                (check the "In Transit" tab)
              </li>
              <li>Click the "Complete Trade" button</li>
            </ol>
            <p className="emphasis">
              If you're happy with how the trade went, say thanks! We're big on gratitude here. 💖
            </p>
          </div>
        </section>

        {/* Section: Spirit of Giving */}
        <section className="article-section">
          <h2>The Spirit of Giving</h2>
          <p>
            TradeMagic is built on a simple observation: Magic collectors live in a world of abundance. You probably have
            hundreds or even thousands of cards sitting in binders and boxes—cards you're not using, cards you'll never
            build decks around, but cards that someone else is actively searching for.
          </p>
          <p>
            This platform is built on the belief that if we create effective systems to move cards from places of
            abundance to scarcity, there's enough to go around for everyone. Everyone benefits when cards flow freely to
            the people who actually want to use them.
          </p>
          <p>
            You could call this platform a marketplace, but it's more accurate to call it a gift economy. Here, you're
            giving cards to people who need them and making space for the community to give to you in kind.
          </p>
          <p className="emphasis">
            We built TradeMagic because that feeling of sending cards to someone who's excited to receive them is
            genuinely rewarding. Having cards you want flow into your mailbox every day of course feels amazing too. This
            is a community where generosity creates abundance for everyone.
          </p>
        </section>

        {/* Section: How Pricing Works */}
        <section className="article-section">
          <h2>How Pricing Works</h2>
          <p>Every card has a point value based on market prices. These values are:</p>
          <ul>
            <li>Updated regularly to reflect current market conditions</li>
            <li>Adjusted based on supply and demand within TradeMagic</li>
            <li>Visible before you initiate any trade</li>
          </ul>
          <p className="conversion">
            <strong>Points use a simple conversion:</strong> 100 points = approximately $1 in trade value
          </p>
        </section>

        {/* Section: Shipping Requirements */}
        <section className="article-section">
          <h2>Shipping Requirements</h2>
          <p>
            <strong>To keep the community safe and reliable:</strong>
          </p>
          <ul className="checklist">
            <li>Ship within 72 hours of initiating a trade</li>
            <li>Use toploaders for all cards</li>
            <li>Add tracking for trades over $50</li>
            <li>
              Package securely - see our{' '}
              <Link to="/articles/shipping" className="article-link">
                Shipping Guide
              </Link>{' '}
              for details
            </li>
          </ul>

          <p>
            <strong>When you receive cards:</strong>
          </p>
          <ul className="checklist">
            <li>Check them immediately - Verify condition, edition, language</li>
            <li>Report problems quickly - Within 48 hours of receiving</li>
          </ul>
        </section>

        {/* Section: Case Resolution */}
        <section className="article-section">
          <h2>When Things Go Wrong: Case Resolution</h2>
          <p>
            Sometimes trades don't go as planned. Maybe the card arrived damaged, or it's the wrong edition, or it never
            arrived at all.
          </p>
          <p>
            <strong>Here's what to do:</strong>
          </p>

          <div className="resolution-step">
            <h3>Step 1: Contact Your Trading Partner</h3>
            <p>
              Most issues can be resolved with a quick message. Maybe they sent the wrong card by accident, or USPS lost
              the package.
            </p>
            <p>Use the messaging system to explain the issue and see if you can work it out together.</p>
          </div>

          <div className="resolution-step">
            <h3>Step 2: Open a Case (If Needed)</h3>
            <p>
              If you can't resolve it directly, click <strong>"Summon Admin"</strong> on the trade.
            </p>
            <p>This notifies our admin team and we'll come assist. Provide:</p>
            <ul>
              <li>
                Photos (if condition/edition is disputed) <em>(this feature coming soon)</em>
              </li>
              <li>Tracking information (if the card is lost)</li>
              <li>Description of what went wrong</li>
            </ul>
          </div>

          <div className="resolution-step">
            <h3>Step 3: Admin Resolution</h3>
            <p>An admin will review the case and mediate a fair resolution. This might include:</p>
            <ul>
              <li>Partial refunds if condition was misrepresented</li>
              <li>Full refunds if the card never arrived</li>
              <li>Requesting replacement cards</li>
              <li>Other solutions based on the situation</li>
            </ul>
            <p className="goal">
              <strong>Our goal:</strong> Fair outcomes that keep both traders whole and maintain community trust.
            </p>
          </div>
        </section>

        {/* Section: Point Fees */}
        <section className="article-section">
          <h2>Point Fees for Economic Sustainability</h2>
          <p>
            In order to responsibly manage the economics of this trading community in a long term sustainable way, it's
            necessary not only to have ways for points to enter the system but ways for trade credits to exit the system
            too.
          </p>
          <p>
            TradeMagic charges a <strong>small variable transaction fee (currently {economicData?.transactionFeePercentage ?? 1}%)</strong> on completed trades.
            This point-fee keeps the economy healthy and stable by preventing inflation. Transaction fees are automatically
            deducted from the amount credited to the sender when you complete a trade.
          </p>
          <p>
            We also charge a <strong>small variable monthly fee (currently {economicData?.monthlyPointFee ?? 10} points)</strong>.
          </p>
          <p>
            These fees are structured in a dynamic way so that it can increase or decrease overtime in response to
            inflationary or deflationary pressures.
          </p>
        </section>

        {/* Section: Trust & Safety */}
        <section className="article-section">
          <h2>Trust & Safety</h2>
          <p>
            <strong>Your security is our priority:</strong>
          </p>
          <ul className="icon-list">
            <li>🔒 Verified accounts - Email and phone verification required</li>
            <li>📦 Tracking required for high-value trades</li>
            <li>⚖️ Fair case resolution process with admin oversight</li>
            <li>🚫 Zero tolerance for scams or misrepresentation</li>
            <li>📊 Transparent metrics - Track your trade history and point balance</li>
          </ul>
        </section>

        {/* Section: Tips for Success */}
        <section className="article-section tips-section">
          <h2>Tips for Success</h2>

          <div className="tip-box">
            <h3>To receive cards faster:</h3>
            <ul>
              <li>
                Keep your{' '}
                <Link to="/my-cards" className="article-link">
                  Want List
                </Link>{' '}
                updated with 30+ cards
              </li>
              <li>Maintain adequate point balance</li>
              <li>Verify your shipping address is accurate</li>
            </ul>
          </div>

          <div className="tip-box">
            <h3>To earn points faster:</h3>
            <ul>
              <li>
                Check the{' '}
                <Link to="/sendcards" className="article-link">
                  Send page
                </Link>{' '}
                daily
              </li>
              <li>Send popular/high-demand cards</li>
              <li>Ship quickly and communicate well</li>
            </ul>
          </div>

          <div className="tip-box">
            <h3>To build reputation:</h3>
            <ul>
              <li>
                <Link to="/receiving" className="article-link">
                  Complete trades
                </Link>{' '}
                promptly
              </li>
              <li>Package cards carefully</li>
              <li>Be honest about condition</li>
              <li>Respond to messages quickly</li>
            </ul>
          </div>
        </section>

        {/* Section: Ready to Start */}
        <section className="article-section cta-section">
          <h2>Ready to Start Trading?</h2>
          <p>That's everything you need to know. The best way to learn is to jump in:</p>
          <ol className="cta-list">
            <li>
              <strong>
                Build your{' '}
                <Link to="/my-cards" className="article-link">
                  Want List
                </Link>
              </strong>{' '}
              - Add 20-30 cards
            </li>
            <li>
              <strong>Verify your account</strong> - Email + shipping address
            </li>
            <li>
              <strong>
                Visit the{' '}
                <Link to="/sendcards" className="article-link">
                  Send Page
                </Link>
              </strong>{' '}
              - Find your first trade
            </li>
          </ol>
          <p className="welcome">
            <strong>Welcome to TradeMagic! Happy Trading</strong>
          </p>
        </section>

        {/* Section: Questions */}
        <section className="article-section footer-section">
          <p>
            Questions? Email{' '}
            <a href="mailto:support@trademagic.gg" className="article-link">
              support@trademagic.gg
            </a>{' '}
            or click the "Feedback" link in the sidebar.
          </p>
        </section>
      </article>

      <Footer />
    </ArticleLayout>
  );
};

export default HowItWorks;
